'use client'

import React, { createContext, useContext, useState, useCallback, useTransition } from 'react'

interface WishlistContextValue {
  wishlist: string[]
  isPending: boolean
  toggle: (productId: string) => Promise<void>
  isWishlisted: (productId: string) => boolean
  count: number
}

const WishlistContext = createContext<WishlistContextValue | null>(null)

export function WishlistProvider({
  children,
  initialWishlist = [],
}: {
  children: React.ReactNode
  initialWishlist?: string[]
}) {
  const [wishlist, setWishlist] = useState<string[]>(initialWishlist)
  const [isPending, startTransition] = useTransition()

  const isWishlisted = useCallback(
    (productId: string) => wishlist.includes(productId),
    [wishlist],
  )

  const toggle = useCallback(
    async (productId: string) => {
      const alreadyWishlisted = wishlist.includes(productId)

      // Optimistic update
      setWishlist((prev) =>
        alreadyWishlisted ? prev.filter((id) => id !== productId) : [...prev, productId],
      )

      startTransition(async () => {
        try {
          if (alreadyWishlisted) {
            const res = await fetch(`/api/wishlist/${productId}`, { method: 'DELETE' })
            if (!res.ok) throw new Error('Failed to remove')
            const data = await res.json()
            setWishlist(data.wishlist)
          } else {
            const res = await fetch('/api/wishlist', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ productId }),
            })
            if (!res.ok) throw new Error('Failed to add')
            const data = await res.json()
            setWishlist(data.wishlist)
          }
        } catch (_err) {
          // Roll back optimistic update on failure
          setWishlist((prev) =>
            alreadyWishlisted ? [...prev, productId] : prev.filter((id) => id !== productId),
          )
        }
      })
    },
    [wishlist],
  )

  return (
    <WishlistContext.Provider
      value={{ wishlist, isPending, toggle, isWishlisted, count: wishlist.length }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export function useWishlist() {
  const ctx = useContext(WishlistContext)
  if (!ctx) throw new Error('useWishlist must be used inside <WishlistProvider>')
  return ctx
}